import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="scope-choice-modal"
//
// Unifies the recurring-instance "this occurrence" vs "this and future
// occurrences" modals (cancel + edit). The radios live inside this
// controller's element as `option` targets; the action button lives outside
// the element (in the modal footer) and is located by id, since Stimulus'
// declarative `data-action` can't reach across the body/footer split.
//
// Two modes (the `mode` value):
//   "relabel"  — cancel flow: as the radios change, swap the submit button's
//                label to match the selected scope. The form submits normally.
//   "navigate" — edit flow: on button click, navigate the drawer frame to the
//                scope-specific edit URL.
//
// Label/URL strings are passed from ERB as Stimulus values so they stay
// i18n-driven (ADR-0019).
export default class extends Controller {
  static targets = ["option", "consequences"];
  static values = {
    mode: String,
    buttonId: String,
    thisOnlyLabel: String,
    futureLabel: String,
    thisOnlyUrl: String,
    futureUrl: String,
    targetFrame: String,
  };

  declare readonly optionTargets: HTMLInputElement[];
  declare readonly consequencesTargets: HTMLElement[];
  declare modeValue: string;
  declare buttonIdValue: string;
  declare thisOnlyLabelValue: string;
  declare futureLabelValue: string;
  declare thisOnlyUrlValue: string;
  declare futureUrlValue: string;
  declare targetFrameValue: string;

  connect() {
    if (this.modeValue === "navigate") {
      // The button is outside this element, so bind it manually and clean up
      // on disconnect (the modal closes by removing this element on success).
      this.button()?.addEventListener("click", this.handleNavigateClick);
    } else {
      // relabel: prime the label to match the initially-checked radio.
      this.update();
    }
  }

  disconnect() {
    // Unconditional: in relabel mode the listener was never added, so this is
    // a harmless no-op. (Reading `modeValue` during teardown is unreliable.)
    this.button()?.removeEventListener("click", this.handleNavigateClick);
  }

  // relabel mode — invoked from the radios' `change->scope-choice-modal#update`.
  update() {
    const scope = this.currentScope();
    this.relabelButton(scope);
    this.toggleConsequences(scope);
  }

  private relabelButton(scope: string | null) {
    const button = this.button();
    if (!button) return;

    const label = scope === "this_and_future" ? this.futureLabelValue : this.thisOnlyLabelValue;

    if (!label) return;

    // Write into the label span when the button has one, so a sibling icon
    // survives. Buttons that are plain text keep the old behaviour.
    const labelEl = button.querySelector<HTMLElement>("[data-scope-choice-label]");
    if (labelEl) {
      labelEl.textContent = label;
    } else {
      button.textContent = label;
    }
  }

  // The consequences describe a wind-down. Under `this_only` they are not
  // merely redundant — one date off the calendar stops no scheduling and keeps
  // nothing "through" a later date — so leaving them up states the opposite of
  // what the cancel is about to do.
  private toggleConsequences(scope: string | null) {
    this.consequencesTargets.forEach((element) => {
      // Tailwind's utility, not the `hidden` attribute: DaisyUI's `.alert` sets
      // `display: grid`, and an author rule outranks the UA rule the attribute
      // leans on — the alert stayed on screen with `hidden` set.
      element.classList.toggle("hidden", scope !== "this_and_future");
    });
  }

  // navigate mode — bound arrow-fn so `this` is the controller when invoked
  // as a listener on the out-of-element footer button.
  handleNavigateClick = (event: Event) => {
    event.preventDefault();

    const scope = this.currentScope();
    if (!scope) return;

    const url = scope === "this_and_future" ? this.futureUrlValue : this.thisOnlyUrlValue;
    if (!url) return;

    // Build a real anchor so Turbo's frame-aware navigation kicks in (a
    // synthetic click on an <a data-turbo-frame> is the cleanest trigger).
    const link = document.createElement("a");
    link.href = url;
    // Which frame the edit form lands in. Defaults to the legacy drawer so
    // any caller that has not opted into the sheet keeps working.
    link.setAttribute("data-turbo-frame", this.targetFrameValue || "drawer_frame");
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Currently-selected scope ("this_only" | "this_and_future"), or null when
  // nothing is checked.
  currentScope(): string | null {
    const checked = this.optionTargets.find((option) => option.checked);
    return checked ? checked.value : null;
  }

  private button(): HTMLElement | null {
    return document.getElementById(this.buttonIdValue);
  }
}
